import React, { useEffect, useMemo } from 'react';
import { Chartjs } from '../../../../components';
import { getChartConfig, getChartTitle } from './utils';
import { setDefaultUrlQueryParamsForSubview, getUrlQueryParams, removeUrlQueryParam, setUrlQueryParam } from '../../../../actions';
import i18n from '../../../../i18n';
import { ClassificationInfo } from '../../../../components/Modals';
import { TrendsLegend } from '../../../../components/MapLegend/subComponents';
import { trends, defaultVariables } from '../../../../referenceData';
import { useModalContext } from '../../../../context/modalContext.jsx';

export default function AbsoluteTrendBarChart({ state, dispatch, urlParams, isTakingScreenshot }) {
  const [showModal] = useModalContext();

  if (!state?.indexedYml) {
    return null;
  }

  if (typeof urlParams.range1from === 'undefined' || typeof urlParams.range1to === 'undefined') {
    setDefaultUrlQueryParamsForSubview(urlParams);
  }

  const shouldBackfillSecondaryRange = typeof urlParams.showClassifications !== 'undefined' && JSON.parse(urlParams.showClassifications) === true && (typeof urlParams.range2from === 'undefined' || typeof urlParams.range2to === 'undefined');

  useEffect(() => {
    if (!shouldBackfillSecondaryRange) {
      return;
    }

    if (typeof urlParams.range2from === 'undefined') {
      dispatch({
        type: 'setUrlQueryParam',
        action: setUrlQueryParam({ name: 'range2from', value: defaultVariables.mid_year }),
      });
    }

    if (typeof urlParams.range2to === 'undefined') {
      dispatch({
        type: 'setUrlQueryParam',
        action: setUrlQueryParam({ name: 'range2to', value: defaultVariables.latest_year }),
      });
    }
  }, [dispatch, shouldBackfillSecondaryRange, urlParams.range2from, urlParams.range2to]);

  // Direct links can enable classifications without the secondary range params.
  // Delay rendering until those params are written back so the first chart render
  // does not calculate classifications from incomplete input.
  if (shouldBackfillSecondaryRange) {
    return null;
  }

  const chartConfig = useMemo(() => getChartConfig(state), [state.setUrlQueryParam, i18n.language]);

  const chartTitle = useMemo(() => getChartTitle(state, urlParams), [state.setUrlQueryParam, i18n.language]);

  const chartId = isTakingScreenshot ? 'screenshotBarChart' : 'bar-chart';

  const isOnMobile = window.innerWidth < 768;

  const chartSize = useMemo(
    () => ({
      height: isOnMobile ? '1200px' : '637px',
    }),
    [],
  );

  function updateChartJsCallback(trendToApplyToChart) {
    const event = new CustomEvent('chartjsEvent', {
      detail: {
        callback: (chart) => {
          chart.options.animations = false;
          chart.data.datasets.forEach((dataset) => {
            if (!dataset.originalBackgroundColor) {
              dataset.originalBackgroundColor = Array.isArray(dataset.backgroundColor) ? [...dataset.backgroundColor] : dataset.backgroundColor;
            }
            // Restore original colors
            dataset.backgroundColor = Array.isArray(dataset.originalBackgroundColor) ? [...dataset.originalBackgroundColor] : dataset.originalBackgroundColor;
          });
          if (trendToApplyToChart !== '') {
            chart.data.datasets.forEach((dataset) => {
              // For bar charts, backgroundColor is an array of colors per bar
              if (Array.isArray(dataset.backgroundColor)) {
                dataset.backgroundColor = dataset.backgroundColor.map((color, index) => {
                  // Check if this bar's trend classification matches
                  const barTrendClassification = dataset.trendClassifications?.[index];
                  if (barTrendClassification !== trendToApplyToChart) {
                    // Fade it by adding transparency (33 = 20% opacity in hex)
                    return color.includes('#') ? color + '33' : color;
                  }
                  return color;
                });
              }
            });
          }

          chart.update(); // Update chart without animation
        },
      },
    });
    document.dispatchEvent(event);
  }

  const legendProps = useMemo(() => {
    const props = {};

    if (typeof urlParams.showClassifications !== 'undefined' && JSON.parse(urlParams.showClassifications) === true) {
      let selectedTrend = urlParams.highlightedTrend || '';
      props.trends = trends();
      props.axisLegend = {
        left: i18n.t('Deterioration'),
        right: i18n.t('Improvement'),
      };
      props.onTrendsClick = (trend, urlParams) => {
        const { highlightedTrend } = urlParams;
        if (typeof highlightedTrend !== 'undefined' && highlightedTrend === trend) {
          removeUrlQueryParam({ name: 'highlightedTrend' });
        } else {
          setUrlQueryParam({ name: 'highlightedTrend', value: trend });
        }
        dispatch({
          type: 'getUrlQueryParams',
          payload: JSON.stringify(getUrlQueryParams()),
        });

        let trendToApplyToChart = '';
        if (selectedTrend === trend) {
          trendToApplyToChart = '';
        } else {
          trendToApplyToChart = trend;
        }
        selectedTrend = trendToApplyToChart;
        updateChartJsCallback(trendToApplyToChart);
      };

      props.onInfoClick = () => {
        showModal(<ClassificationInfo title={i18n.t('trend classifications')} />);
      };
    }
    return props;
  }, [state.setUrlQueryParam, i18n.language]);

  useEffect(() => {
    if (urlParams.highlightedTrend) {
      updateChartJsCallback(urlParams.highlightedTrend);
    }
  }, []);

  return (
    <>
      <Chartjs chartTitle={chartTitle} chartConfig={chartConfig} chartId={chartId} chartSize={chartSize} isTakingScreenshot={isTakingScreenshot} />
      {legendProps.trends && <TrendsLegend className="forGraph" urlParams={urlParams} axisLegend={legendProps.axisLegend} trends={legendProps.trends} onTrendsClick={legendProps.onTrendsClick} isTakingScreenshot={isTakingScreenshot} onInfoClick={legendProps.onInfoClick} />}
    </>
  );
}
